import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import VirtualizedList from './VirtualizedList';
import BulkDeleteModal from './BulkDeleteModal';
import BulkMoveModal from './BulkMoveModal';
import FilePreviewModal from './FilePreviewModal';
import FileItem, { S3Object } from './FileItem';
import BulkActionBar from './BulkActionBar';
import CreateFolderModal from './CreateFolderModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useCache } from '../hooks/useCache';
import { useTransfer } from '../contexts/TransferContext';
import { useFileSelection } from '../hooks/useFileSelection';
import { useDownloadJob, MAX_BATCH_ZIP_SIZE } from '../hooks/useDownloadJob';
import { useBulkOperations } from '../hooks/useBulkOperations';
import { invalidateAndRefresh } from '../utils/invalidateAndRefresh';
import { useSearch } from '../hooks/useSearch';
import SearchPanel from './SearchPanel';
import { isHardArchiveStorageClass, isSoftArchiveStorageClass } from '../constants/storageClasses';
export { MAX_BATCH_ZIP_SIZE };
interface FileBrowserProps {
  bucketName: string;
  s3Client: any;
  permissions: {
    list: boolean;
    download: boolean;
    /** Optional: records written before the download/preview split omit this.
     *  Resolved via `canPreviewFiles` below, which falls back to `download`. */
    preview?: boolean;
    upload: boolean;
    delete: boolean;
    maxObjectSize: number;
    monthlyDataTransferCap: number;
  };
  refreshTrigger?: number;
  onPrefixChange?: (prefix: string) => void;
  initialFolderPath?: string; // For folder-level permission restrictions
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

/**
 * Returns true iff the ISO-8601 timestamp is strictly in the past.
 * Boundary (exact match to current ms) is treated as not expired (strict >).
 */
export function isExpired(urlExpiresAt: string): boolean {
  return Date.now() > new Date(urlExpiresAt).getTime();
}

const FileBrowser: React.FC<FileBrowserProps> = ({ bucketName, permissions, refreshTrigger, onPrefixChange, initialFolderPath }) => {
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [folders, setFolders] = useState<S3Object[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof S3Object;
    direction: 'asc' | 'desc';
  } | null>(null);

  const [prefix, setPrefix] = useState(initialFolderPath || '');
  const [restrictedToFolder, setRestrictedToFolder] = useState<string | null>(initialFolderPath || null);
  const [previewModal, setPreviewModal] = useState<{ url: string; type: 'image' | 'video'; name: string } | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ key: string; fileName: string } | null>(null);
  const refreshTriggerRef = useRef(refreshTrigger);
  // Effective preview permission. Preview was split out of download, so permission
  // records written before the split carry no `preview` field — fall back to
  // `download` for those rather than silently disabling preview. Mirrors
  // hasPreviewPermission() in backend/functions/s3-proxy/services/permissions.js.
  // Resolved here, not at the caller, so every consumer of FileBrowser gets it.
  const canPreviewFiles = permissions.preview ?? permissions.download;
  const previewUrlCache = useRef<Record<string, string>>({});
  // Stable reference — writing to a ref never needs to change, so this callback
  // never causes FileItem's useCallback/useEffect to re-fire.
  const handleThumbnailLoaded = useCallback((key: string, url: string) => {
    previewUrlCache.current[key] = url;
  }, []);
  const initialFolderPathRef = useRef(initialFolderPath);
  const [isTruncated, setIsTruncated] = useState(false);
  const [nextContinuationToken, setNextContinuationToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const effectivePrefix = initialFolderPathRef.current !== initialFolderPath
    ? (initialFolderPath || '') : prefix;

  // Use a ref so the fetcher always reads the latest effectivePrefix value.
  // Without this, the async fetcher closure can capture a stale effectivePrefix
  // from a previous render, causing the wrong folder to be listed after navigation.
  const effectivePrefixRef = useRef(effectivePrefix);
  effectivePrefixRef.current = effectivePrefix;

  // useCache's fetcher takes no arguments, so it cannot tell an explicit Refresh
  // click from an initial mount / navigation fetch. This ref is the one-shot channel
  // between the Refresh handler and the fetcher: the handler sets it, the fetcher
  // reads and immediately clears it. Without the immediate clear the flag could leak
  // into a later unrelated fetch, and if it were always set every mount would bypass
  // the backend list cache — making the cache pointless.
  const forceRevalidateRef = useRef<boolean>(false);

  const cacheKey = `objects-${bucketName}-${effectivePrefix}-initial-${initialFolderPath || 'root'}`;
  const { data: cachedObjects, loading: cacheLoading, refresh: refreshCache } = useCache(
    cacheKey,
    async () => {
      // Consume-and-reset: read once, clear immediately so it can never leak.
      const forceRevalidate = forceRevalidateRef.current;
      forceRevalidateRef.current = false;
      // Read from ref so we always use the latest prefix, not a stale closure value.
      const currentPrefix = effectivePrefixRef.current;
      // Encode each segment individually so slashes remain real URL path separators.
      // encodeURIComponent on the whole prefix would encode '/' as '%2F', turning
      // multi-level folder paths into a single URL segment that the router can't split.
      const encodedPrefix = currentPrefix
        ? currentPrefix.split('/').map(s => encodeURIComponent(s)).join('/')
        : '';
      const basePath = encodedPrefix
        ? `/s3/list/${bucketName}/${encodedPrefix}`
        : `/s3/list/${bucketName}`;
      const apiPath = forceRevalidate ? `${basePath}?refresh=1` : basePath;
      const response = await apiCall(apiPath);
      return {
        folders: response.folders || [],
        files: response.files || [],
        restrictedToFolder: response.restrictedToFolder || null,
        isTruncated: response.isTruncated || false,
        nextContinuationToken: response.nextContinuationToken || null,
      };
    },
    { ttl: 5 * 60 * 1000 }
  );

  /**
   * Explicit user-initiated Refresh. Sets the one-shot flag so this fetch carries
   * `?refresh=1`, which makes the backend re-read the prefix from S3 and rewrite its
   * cache entry. Deliberately NOT used by mount/navigation, `refreshTrigger`, or the
   * post-mutation `invalidateAndRefresh` path — those already get fresh data.
   */
  const handleRefreshClick = useCallback((): void => {
    forceRevalidateRef.current = true;
    refreshCache().catch(() => {
      // Reset so a failed refresh cannot leave the flag set for a later fetch.
      forceRevalidateRef.current = false;
    });
  }, [refreshCache]);

  useEffect(() => {
    if (cachedObjects) {
      setFolders(cachedObjects.folders);
      setObjects(cachedObjects.files);
      if (cachedObjects.restrictedToFolder) {
        setRestrictedToFolder(cachedObjects.restrictedToFolder);
      }
      setIsTruncated(cachedObjects.isTruncated || false);
      setNextContinuationToken(cachedObjects.nextContinuationToken || null);
    }
  }, [cachedObjects]);

  // Reset state when bucket or folder changes
  useEffect(() => {
    initialFolderPathRef.current = initialFolderPath;
    setPrefix(initialFolderPath || '');
    setRestrictedToFolder(initialFolderPath || null);
    setSearchTerm('');
    setIsTruncated(false);
    setNextContinuationToken(null);
  }, [bucketName, initialFolderPath]);

  useEffect(() => {
    if (refreshTrigger !== refreshTriggerRef.current && permissions.list) {
      refreshTriggerRef.current = refreshTrigger;
      refreshCache();
    }
  }, [refreshTrigger, permissions.list, refreshCache]);

  const filteredAndSortedItems = useMemo(() => {
    let allItems = [...folders, ...objects];
    if (searchTerm) {
      allItems = allItems.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (sortConfig) {
      allItems.sort((a, b) => {
        const aValue = a[sortConfig.key] ?? '';
        const bValue = b[sortConfig.key] ?? '';
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      allItems.sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      });
    }
    return allItems;
  }, [folders, objects, searchTerm, sortConfig]);

  const {
    selectedFiles,
    selectedCount,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
  } = useFileSelection({ items: filteredAndSortedItems });

  const hasHardArchiveSelected = useMemo(() =>
    Array.from(selectedFiles).some(key => {
      const obj = [...folders, ...objects].find(o => o.key === key);
      return obj ? isHardArchiveStorageClass(obj.storageClass) : false;
    }),
  [selectedFiles, folders, objects]);

  const hasSoftArchiveSelected = useMemo(() =>
    !hasHardArchiveSelected &&
    Array.from(selectedFiles).some(key => {
      const obj = [...folders, ...objects].find(o => o.key === key);
      return obj ? isSoftArchiveStorageClass(obj.storageClass) : false;
    }),
  [hasHardArchiveSelected, selectedFiles, folders, objects]);

  const {
    isDownloading,
    startDownload,
    cancelDownload,
    checkActiveJob,
  } = useDownloadJob({ bucketName, prefix: effectivePrefix, onSelectionClear: clearSelection });

  useEffect(() => {
    checkActiveJob();
  }, [bucketName, checkActiveJob]);

  const {
    showBulkDeleteModal,
    showBulkMoveModal,
    openDeleteModal,
    closeDeleteModal,
    openMoveModal,
    closeMoveModal,
    handleBulkDeleteSuccess,
    handleBulkMoveSuccess,
  } = useBulkOperations({
    bucketName,
    selectedFiles,
    onSuccess: () => invalidateAndRefresh(bucketName, refreshCache),
  });

  /**
   * Deletes one folder row by routing it through the existing bulk-delete flow:
   * select just that folder, then open the existing BulkDeleteModal.
   *
   * Folder deletion must go through `POST /s3/bulk-delete/:bucket`, NOT through
   * `showDeleteConfirmation` / `DELETE /s3/delete/:bucket/:key` — API Gateway strips
   * trailing slashes, so that path cannot carry a folder prefix.
   *
   * Exposing this control closes a UI REACHABILITY gap, not an authorization hole:
   * the backend re-resolves the caller's permission on every request via
   * `getUserPermission` and re-checks folder scope for each enumerated key, so
   * showing or hiding a control here grants nothing.
   */
  const handleFolderDeleteClick = useCallback((folderKey: string): void => {
    clearSelection();
    toggleSelection(folderKey);
    openDeleteModal();
  }, [clearSelection, toggleSelection, openDeleteModal]);

  // useTransfer is still needed for single-file downloads
  const { registerTransfer } = useTransfer();

  const {
    query,
    setQuery,
    results: searchResults,
    isLoading: searchLoading,
    isSearchMode,
    isCapped,
    isTimedOut,
    error: searchError,
    clearSearch,
  } = useSearch(bucketName, restrictedToFolder);

  const downloadFile = async (key: string) => {
    if (!permissions.download) return;
    const cleanupTransfer = registerTransfer({
      id: `download:${bucketName}:${key}:${Date.now()}`,
      type: 'download',
      bucket: bucketName,
      key,
    });
    try {
      const response = await apiCall(`/s3/download/${bucketName}/${key.split('/').map(s => encodeURIComponent(s)).join('/')}`);
      if (response.fileSize > 1_073_741_824) {
        const sizeGb = (response.fileSize / 1_073_741_824).toFixed(1);
        toast(`File is ${sizeGb} GB — download may take several minutes`);
      }
      const link = document.createElement('a');
      link.href = response.downloadUrl;
      link.download = key.split('/').pop() || key;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download file');
    } finally {
      cleanupTransfer();
    }
  };

  const handleSearchNavigate = useCallback(async (key: string) => {
    const parentFolder = key.includes('/')
      ? key.substring(0, key.lastIndexOf('/') + 1)
      : '';

    // Scope-containment check: if folder-restricted, parent must start with allowed prefix
    if (restrictedToFolder && parentFolder !== '' && !parentFolder.startsWith(restrictedToFolder)) {
      return; // suppress silently per Req 4.3
    }

    const previousQuery = query;
    clearSearch();

    try {
      setPrefix(parentFolder);
      onPrefixChange?.(parentFolder);
      await refreshCache();
    } catch (err: unknown) {
      // Navigation failed — restore search state per Req 4.1
      setQuery(previousQuery);
      toast.error(err instanceof Error ? err.message : 'Failed to navigate to folder');
    }
  }, [query, setQuery, restrictedToFolder, clearSearch, onPrefixChange, refreshCache]);

  // Surface search errors as toasts (SearchPanel stays visible while error is shown)
  useEffect(() => {
    if (searchError) {
      toast.error(searchError);
    }
  }, [searchError]);

  // Load more handler for pagination
  const loadMore = async () => {
    if (!nextContinuationToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const encodedPrefix = effectivePrefixRef.current
        ? effectivePrefixRef.current.split('/').map(s => encodeURIComponent(s)).join('/')
        : '';
      const basePath = encodedPrefix ? `/s3/list/${bucketName}/${encodedPrefix}` : `/s3/list/${bucketName}`;
      const response = await apiCall(`${basePath}?continuationToken=${encodeURIComponent(nextContinuationToken)}`);
      setFolders((prev) => [...prev, ...(response.folders || [])]);
      setObjects((prev) => [...prev, ...(response.files || [])]);
      setIsTruncated(response.isTruncated || false);
      setNextContinuationToken(response.nextContinuationToken || null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load more files');
    } finally {
      setLoadingMore(false);
    }
  };

  const showDeleteConfirmation = (key: string, fileName: string) => {
    if (!permissions.delete) return;
    setDeleteConfirmation({ key, fileName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    try {
      await apiCall(`/s3/delete/${bucketName}/${encodeURIComponent(deleteConfirmation.key)}`, {
        method: 'DELETE'
      });

      toast.success(`File "${deleteConfirmation.fileName}" deleted successfully`);
      setDeleteConfirmation(null);
      invalidateAndRefresh(bucketName, refreshCache);
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
      setDeleteConfirmation(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation(null);
  };

  // Cache signed preview URLs by object key for the component's lifetime.
  // getThumbnail issues a 1-hour CloudFront signed URL each time it is called;
  // without this cache every open-modal click triggers a fresh Lambda call and
  // forces the browser (and CloudFront) to re-fetch the full file even though the
  // underlying object has not changed.
  const previewFile = async (object: S3Object) => {
    // Preview is its own permission now - it no longer implies download.
    if (!canPreviewFiles) return;

    try {
      let url = previewUrlCache.current[object.key];
      if (!url) {
        // preview=true marks this as an intentional user preview so it's audited,
        // as opposed to the thumbnail fetches that happen while browsing.
        // Each key segment is encoded individually so '/' separators survive.
        const response = await apiCall(`/s3/thumbnail/${bucketName}/${object.key.split('/').map(s => encodeURIComponent(s)).join('/')}?preview=true`);
        url = response.thumbnailUrl as string;
        previewUrlCache.current[object.key] = url;
      }
      setPreviewModal({
        url,
        type: object.isVideo ? 'video' : 'image',
        name: object.name,
      });
    } catch (error) {
      console.error('Error getting preview URL:', error);
      toast.error('Failed to load preview');
    }
  };

  const navigateToFolder = (folderKey: string) => {
    setPrefix(folderKey);
    onPrefixChange?.(folderKey);
  };

  const navigateToPath = (path: string) => {
    if (restrictedToFolder && !path.startsWith(restrictedToFolder) && path !== '' && path !== restrictedToFolder) {
      return;
    }
    if (restrictedToFolder && path === '') {
      setPrefix(restrictedToFolder);
      onPrefixChange?.(restrictedToFolder);
      return;
    }
    setPrefix(path);
    onPrefixChange?.(path);
  };

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [];
    if (restrictedToFolder) {
      breadcrumbs.push({ name: bucketName, path: '' });
      const restrictedParts = restrictedToFolder.split('/').filter(p => p);
      let currentPath = '';
      restrictedParts.forEach(part => {
        currentPath += part + '/';
        breadcrumbs.push({ name: part, path: currentPath });
      });
      if (prefix && prefix !== restrictedToFolder && prefix.startsWith(restrictedToFolder)) {
        const additionalParts = prefix.substring(restrictedToFolder.length).split('/').filter(p => p);
        additionalParts.forEach(part => {
          currentPath += part + '/';
          breadcrumbs.push({ name: part, path: currentPath });
        });
      }
    } else {
      breadcrumbs.push({ name: bucketName, path: '' });
      if (prefix) {
        let currentPath = '';
        prefix.split('/').filter(p => p).forEach(part => {
          currentPath += part + '/';
          breadcrumbs.push({ name: part, path: currentPath });
        });
      }
    }

    return breadcrumbs;
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const folderPath = prefix + newFolderName.trim();

      await apiCall(`/s3/create-folder/${bucketName}`, {
        method: 'POST',
        body: JSON.stringify({
          folderPath: folderPath
        })
      });

      toast.success(`Folder "${newFolderName}" created successfully`);
      setNewFolderName('');
      setShowCreateFolder(false);
      invalidateAndRefresh(bucketName, refreshCache);
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!permissions.list) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No List Permission</h3>
          <p className="text-gray-500">You don't have permission to browse files in this bucket.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* File Browser */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden border-2 border-gray-200 hover:border-gray-300 transition-all duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          {/* Breadcrumbs */}
          <div className="flex items-center space-x-2 mb-4">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                {getBreadcrumbs().map((breadcrumb, index) => {
                  const isLast = index === getBreadcrumbs().length - 1;
                  const isClickable = !isLast && (!restrictedToFolder || 
                    breadcrumb.path === restrictedToFolder || 
                    (breadcrumb.path.startsWith(restrictedToFolder) && breadcrumb.path !== ''));
                  
                  return (
                    <li key={breadcrumb.path} className="flex items-center">
                      {index > 0 && (
                        <svg className="flex-shrink-0 h-4 w-4 text-gray-400 mx-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      <button
                        onClick={() => isClickable ? navigateToPath(breadcrumb.path) : null}
                        disabled={!isClickable}
                        className={`text-sm font-medium transition-colors ${
                          isLast
                            ? 'text-gray-900 cursor-default'
                            : isClickable
                              ? 'text-blue-600 hover:text-blue-800'
                              : 'text-gray-400 cursor-not-allowed'
                        }`}
                        title={!isClickable && restrictedToFolder ? 'Access restricted to folder' : undefined}
                      >
                        {breadcrumb.name}
                      </button>
                      {/* Show lock icon for restricted folder root */}
                      {restrictedToFolder && breadcrumb.path === restrictedToFolder && (
                        <span className="ml-1 text-blue-500" title="Your access is restricted to this folder">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
            {/* Folder restriction indicator */}
            {restrictedToFolder && (
              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Folder Access
              </span>
            )}
          </div>

          {/* Sort */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <label htmlFor="file-search-input" className="sr-only">Search files</label>
                <input
                  id="file-search-input"
                  type="text"
                  value={isSearchMode ? query : searchTerm}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchTerm(val);
                    setQuery(val);
                  }}
                  aria-label="Search files"
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search files and folders..."
                />
                {searchLoading && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <div
                      className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"
                      role="status"
                      aria-label="Searching"
                    />
                  </div>
                )}
                {query.length === 1 && (
                  <p className="mt-1 text-xs text-gray-500">Min. 2 characters</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <select
                  value={sortConfig ? `${sortConfig.key}-${sortConfig.direction}` : 'default'}
                  onChange={(e) => {
                    if (e.target.value === 'default') {
                      setSortConfig(null);
                    } else {
                      const [key, direction] = e.target.value.split('-');
                      setSortConfig({
                        key: key as keyof S3Object,
                        direction: direction as 'asc' | 'desc'
                      });
                    }
                  }}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="default">Default Sort</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="size-asc">Size (Small to Large)</option>
                  <option value="size-desc">Size (Large to Small)</option>
                  <option value="lastModified-desc">Newest First</option>
                  <option value="lastModified-asc">Oldest First</option>
                </select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                {searchTerm ? (
                  <>
                    {filteredAndSortedItems.length} of {folders.length + objects.length} items
                    {searchTerm && (
                      <span className="ml-2 text-blue-600">
                        matching "{searchTerm}"
                      </span>
                    )}
                  </>
                ) : (
                  `${folders.length + objects.length} items`
                )}
                {selectedCount > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    • {selectedCount} item{selectedCount > 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              {(folders.length > 0 || objects.length > 0) && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
                      }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {permissions.upload && (
                <button
                  onClick={() => setShowCreateFolder(true)}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Folder
                </button>
              )}
              <button
                onClick={handleRefreshClick}
                disabled={cacheLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <svg className={`w-4 h-4 mr-2 ${cacheLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Bulk Action Bar */}
          <BulkActionBar
            selectedCount={selectedCount}
            totalCount={filteredAndSortedItems.length}
            isDownloading={isDownloading}
            permissions={{ download: permissions.download, delete: permissions.delete }}
            onDownload={() => {
              if (hasHardArchiveSelected) {
                toast.error('Selection contains archive files. Remove them to download.');
                return;
              }
              startDownload(Array.from(selectedFiles).map(key => ({ key, isFolder: folders.some(f => f.key === key) })));
            }}
            onMove={() => {
              if (hasHardArchiveSelected || hasSoftArchiveSelected) {
                toast.error('Selection contains archive files. Remove them to move.');
                return;
              }
              openMoveModal();
            }}
            onDelete={() => {
              if (hasHardArchiveSelected) {
                toast.error('Selection contains archive files. Remove them to delete.');
                return;
              }
              openDeleteModal();
            }}
            onSelectAll={selectAll}
            onClearSelection={clearSelection}
            onCancelDownload={cancelDownload}
            hasHardArchiveSelected={hasHardArchiveSelected}
            hasSoftArchiveSelected={hasSoftArchiveSelected}
          />
        </div>

        {/* Content */}
        <div className="p-6 min-h-96">
          {isSearchMode ? (
            <SearchPanel
              results={searchResults}
              query={query}
              isLoading={searchLoading}
              isCapped={isCapped}
              isTimedOut={isTimedOut}
              error={searchError}
              canDownload={permissions.download}
              onNavigate={handleSearchNavigate}
              onDownload={downloadFile}
            />
          ) : (
            <>
              {cacheLoading && !cachedObjects ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Loading files...</p>
                </div>
              ) : filteredAndSortedItems.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {searchTerm ? 'No matching files found' : (permissions.upload ? 'No files yet' : 'Folder is empty')}
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">
                    {searchTerm ? (
                      <>
                        No files or folders match "{searchTerm}". Try adjusting your search terms.
                        <button
                          onClick={() => setSearchTerm('')}
                          className="block mx-auto mt-2 text-blue-600 hover:text-blue-800"
                        >
                          Clear search
                        </button>
                      </>
                    ) : (
                      permissions.upload
                        ? 'Drag and drop files here, click to browse, or create a new folder to get started.'
                        : 'This folder contains no files or you may not have permission to view them.'
                    )}
                  </p>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {filteredAndSortedItems.map((item) => (
                    item.isFolder ? (
                      <div
                        key={item.key}
                        className={`group relative bg-white border-2 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 ${isSelected(item.key) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                      >
                        {/* Selection is a prerequisite for BOTH folder ZIP download and folder
                            delete, so the checkbox is offered when either is permitted. Each
                            action stays gated on its own permission in BulkActionBar. */}
                        {(permissions.download || permissions.delete) && (
                          <div className="absolute top-2 left-2 z-10">
                            <input
                              type="checkbox"
                              checked={isSelected(item.key)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelection(item.key);
                              }}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </div>
                        )}
                        {/* Folder delete — reaches the bulk-delete route via handleFolderDeleteClick */}
                        {permissions.delete && (
                          <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button
                              type="button"
                              aria-label={`Delete folder ${item.name.replace('/', '')}`}
                              title="Delete this folder and everything inside it"
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleFolderDeleteClick(item.key);
                              }}
                              className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                        <div 
                          className="flex flex-col items-center text-center cursor-pointer"
                          onClick={() => navigateToFolder(item.key)}
                        >
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-3 group-hover:from-blue-600 group-hover:to-blue-700 transition-colors">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-gray-900 truncate w-full" title={item.name}>
                            {item.name.replace('/', '')}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Folder</p>
                        </div>
                      </div>
                    ) : (
                      <FileItem
                        key={item.key}
                        object={item}
                        onDownload={() => downloadFile(item.key)}
                        onPreview={() => previewFile(item)}
                        onDelete={() => showDeleteConfirmation(item.key, item.name)}
                        onThumbnailLoaded={handleThumbnailLoaded}
                        canDownload={permissions.download}
                        canPreview={canPreviewFiles}
                        canDelete={permissions.delete}
                        bucketName={bucketName}
                        formatFileSize={formatFileSize}
                        viewMode={viewMode}
                        isSelected={isSelected(item.key)}
                        onToggleSelect={() => toggleSelection(item.key)}
                      />
                    )
                  ))}
                </div>
              ) : (
                <VirtualizedList
                  items={filteredAndSortedItems}
                  itemHeight={80}
                  containerHeight={600}
                  className="border border-gray-200 rounded-lg"
                  renderItem={(item, _index) => (
                    item.isFolder ? (
                      <div
                        key={item.key}
                        className={`flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 transition-all duration-200 group ${isSelected(item.key) ? 'bg-blue-50' : ''}`}
                        style={{ height: 80 }}
                      >
                        <div className="flex items-center space-x-4">
                          {/* Selection is a prerequisite for BOTH folder ZIP download and folder
                              delete, so the checkbox is offered when either is permitted. Each
                              action stays gated on its own permission in BulkActionBar. */}
                          {(permissions.download || permissions.delete) && (
                            <input
                              type="checkbox"
                              checked={isSelected(item.key)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelection(item.key);
                              }}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
                            />
                          )}
                          <div 
                            className="flex items-center space-x-4 cursor-pointer flex-1"
                            onClick={() => navigateToFolder(item.key)}
                          >
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:from-blue-600 group-hover:to-blue-700 transition-colors">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.name.replace('/', '')}</p>
                              <p className="text-xs text-gray-500">Folder</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        {/* Folder delete — reaches the bulk-delete route via handleFolderDeleteClick */}
                        {permissions.delete && (
                          <div className="flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button
                              type="button"
                              aria-label={`Delete folder ${item.name.replace('/', '')}`}
                              title="Delete this folder and everything inside it"
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleFolderDeleteClick(item.key);
                              }}
                              className="text-sm px-3 py-1 border rounded-lg transition-colors text-red-600 hover:text-red-800 border-red-600 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      // The key belongs on this wrapper, not on FileItem:
                      // VirtualizedList puts whatever renderItem returns straight
                      // into an array child, so React reconciles on the wrapper.
                      // A key on the inner element leaves the array unkeyed and
                      // lets React reuse the wrong row when the list reorders.
                      <div key={item.key} style={{ height: 80 }}>
                        <FileItem
                          object={item}
                          onDownload={() => downloadFile(item.key)}
                          onPreview={() => previewFile(item)}
                          onDelete={() => showDeleteConfirmation(item.key, item.name)}
                          onThumbnailLoaded={handleThumbnailLoaded}
                          canDownload={permissions.download}
                          canPreview={canPreviewFiles}
                          canDelete={permissions.delete}
                          bucketName={bucketName}
                          formatFileSize={formatFileSize}
                          viewMode="list"
                          isSelected={isSelected(item.key)}
                          onToggleSelect={() => toggleSelection(item.key)}
                        />
                      </div>
                    )
                  )}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <CreateFolderModal
          folderName={newFolderName}
          onFolderNameChange={setNewFolderName}
          onConfirm={createFolder}
          onCancel={() => { setShowCreateFolder(false); setNewFolderName(''); }}
        />
      )}

      {/* Preview Modal */}
      <FilePreviewModal
        isOpen={!!previewModal}
        onClose={() => setPreviewModal(null)}
        url={previewModal?.url ?? ''}
        type={previewModal?.type ?? 'image'}
        name={previewModal?.name ?? ''}
      />

      {/* Load More */}
      {isTruncated && (
        <div className="flex justify-center mt-4 px-6 pb-6">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="btn-secondary px-4 py-2 disabled:opacity-50"
          >
            {loadingMore ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Loading...
              </span>
            ) : (
              'Load more'
            )}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <DeleteConfirmModal
          fileName={deleteConfirmation.fileName}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <BulkDeleteModal
          bucketName={bucketName}
          selectedFiles={selectedFiles}
          onSuccess={handleBulkDeleteSuccess}
          onError={(msg) => toast.error(msg)}
          onCancel={closeDeleteModal}
        />
      )}

      {/* Bulk Move Modal */}
      {showBulkMoveModal && (
        <BulkMoveModal
          bucketName={bucketName}
          selectedFiles={selectedFiles}
          onSuccess={handleBulkMoveSuccess}
          onError={(msg) => toast.error(msg)}
          onCancel={closeMoveModal}
        />
      )}
    </>
  );
};
export default FileBrowser;
